import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { fmtNumber } from '../../utils/region';
import { menuPill, tabColor, lighten } from '../../tabPalette';
import { pageHref, openInNewTab } from '../../services/newTab';   // right-click = a tab of its own (report ab347131)
import type { UserPage } from '../../types';

// ── User page (report edb33cb7) ───────────────────────────────────────────────────────────────────────
// "Create a user page that can be launched from the quick launch menu": one of the pages a Quick Launch dashboard can
// hold (FocusDashboard's picker offers it to everyone), so it opens from the side column, the top bar or the dock alike.
// It counts one person's work — the jobs they are a resource on and how many were done by the job's finish date, the
// quotations they created and how many a receipt was issued for (directly, or through an invoice issued from them —
// 5th opening: conversion is measured by receipts, not invoices), the invoices they created and how many are paid
// (report d14ad11b), the leads they answered and how many turned into a quotation (that report's 9th opening; its 10th
// gave that figure a block of its own, Emails — Calem: "make a new block for the email function rather than including
// it in the quotations block" — and its 11th moved that block ahead of Quotations: "Change the order so that the email
// block is before the quotations block", leaving the row Jobs · Emails · Quotations · Invoices · System suggestions ·
// System bugs), the suggestions they made in Bugify and how many were implemented, and the bugs they found (server
// routes/userPage.ts) — under two circular dials, Productivity and Creativity, each a score out of 100.
// Decided with the reporter: each person sees their own page and an admin anyone's, from the Person picker that only an
// admin gets. The scores (7th opening, the scoring confirmed) are on trial: a dial shows one only on the page of a person
// an admin has ticked 'Show scores (trial)' for, beside the Person picker, and only from 5 jobs (Productivity) or 5
// suggestions and bugs (Creativity) up; anywhere else it reads "Not enough data yet". How each is worked out: the server.
// Centred on the page (4th opening): the 1694px column sits in the page's middle, with its headings, dials and stat cards
// centred in it. It was 920px; report 6e2aad53's 2nd opening widened it to 1100px so the four stat cards of the time fit
// on one line, and the five it held from report d14ad11b — the Invoices block, counting the invoices the person issued
// and how many are paid — still did (see statBlock). 1460px was that report's 2nd opening (Calem: "Make each column
// 60-80 pixels wider so none of the information is squashed and/or wrapping"): a fifth of 1100px left each block 210px,
// too narrow for the longest labels — 'Done on/before finish date', 'Found and reported in Bugify' and the 'System
// suggestions' heading all broke onto a second line, which left the heading bubbles at different heights. 1694px is its
// 10th opening ("Reduce the width by 10 pixels for each block and make a new block for the email function rather than
// including it in the quotations block"): six blocks, each 10px narrower than the five were — a sixth of 1694px is
// 272px where a fifth of 1460px was 282px — plus their five 12px gaps. The row's own layout has never been touched; the
// column alone carries what a block gains or loses.
// A manager's page may also carry a team (report 8832878a: "please add a team job conversion bubble on my user page"): the
// members of staff an admin picks for that page in 'Team', beside the Person picker. Each of those blocks shows the
// team's figures under the person's, counted the same way — a job two members share counted once (report 7f6e809c, 3rd
// opening; until then the team had a Team jobs block of its own, its jobs only, with its finishing score). That score, on
// trial as the dials are, has a dial of its own, Team productivity, between the person's Productivity and Creativity
// (report 52cf6f71, 2nd opening: "Show a completely separate productivity indicator for the user versus the team"); its
// 1st opening had put it in the Productivity dial, under the person's own past a short grey line.

const DIAL = 172;   // a dial's side, px (156 before report 52cf6f71)
const RING = 12;    // its grey track's width
const ARC = 18;     // its coloured arc's, wider than the track (report 52cf6f71, 3rd opening); it was the track's 12
const HEAD_W = 22;  // the arrowhead at the arc's end: its width across the ring, a little over the arc's
const HEAD_L = 12;  // and how far its point reaches on round the ring past the arc's end

// A dial's arc runs through the score bands' colours as it goes round, each blending into the next (report 52cf6f71:
// "The first 25% is red. 26% to 50% can be orange. 51% to 65% can be yellow. 65% to 80% can be light green. Above 80% can
// be dark green"; 2nd opening: "Show the colors progressively landing into the next color as it goes around the circle,
// not just one color"). Each colour is pure at its point below — red at the top, the others about the middle of their
// band, dark green from 90 on — and between two points the arc goes over from one to the next. The 1st opening drew the
// whole arc in its score's band's colour; before that it was the accent green whatever the score.
const DIAL_STOPS: [number, string][] = [[0, '#DC2626'], [38, '#EA580C'], [58, '#EAB308'], [73, '#4ADE80'], [90, '#15803D']];
const hexRgb = (c: string) => [1, 3, 5].map(i => parseInt(c.slice(i, i + 2), 16));
const dialColor = (p: number): string => {   // the arc's colour `p` points of the way round (0–100)
  const k = DIAL_STOPS.findIndex(([at]) => at > p);
  if (k < 1) return DIAL_STOPS[k < 0 ? DIAL_STOPS.length - 1 : 0][1];
  const [[a, ca], [b, cb]] = [DIAL_STOPS[k - 1], DIAL_STOPS[k]];
  const [from, to] = [hexRgb(ca), hexRgb(cb)];
  const f = (p - a) / (b - a);
  return `#${from.map((c, i) => Math.round(c + (to[i] - c) * f).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
};

// The colours of a page's bubble in the menu, by the page's key; each stat card wears its page's (report 6e2aad53:
// "Change the colors of each bubble to their relevant bubble colors (including background) in the menu"). A page with a
// top-menu button takes its box's (menuPill, unselected): the Jobs card the Jobs box's pink wash and line, the
// Quotations and Invoices cards the blue wash #E6F1FB and line of the box those two buttons share, and the Emails card
// (report d14ad11b, 10th opening) the purple wash and line of the box the Email page shares with Contacts and
// Calendar, so the block reads as the email module's. A page with none
// takes the green line and icon of its look in a Quick Launch menu (pagePill in FocusDashboard, report 020e6911):
// Bugify's, which Suggestions and Bugs belong to — on the light green wash --accent-bg #E1F5EE where that button is
// white (2nd opening: "Add a light green background in the bugs and suggestions box"). `color`, the solid colour, goes
// to the icon in its heading only; the words stay dark, to read on every wash. All four cards of the time were white
// with a grey line and a green icon.
const menuBubble = (k: string): React.CSSProperties => tabColor(k) ? menuPill(k, false)
  : { borderColor: 'var(--accent)', background: 'var(--accent-bg)', color: 'var(--accent)' };
// The light green wash the Bugify cards take, written out: menuBubble hands it over as the CSS variable
// --accent-bg, which lighten cannot read, and the heading bubbles need a colour it can (see statBlock). The same
// value as the variable in global.css, and as the Bank box's wash.
const ACCENT_WASH = '#E1F5EE';

// The seam that cuts a stat card in half, the person's figures over the team's (Calem, report 81b2c692: "on every block
// cut it in half with the same color stitching. Top half for my results, bottom half for team results"): the card's own
// line, stitched, run out through its 16px side padding to both its edges, so the block reads as one bubble cut in two
// rather than as two lists stacked. "The same color" is each block's own, so there are six seams in four colours —
// the Jobs card's pink, the Quotations and Invoices cards' blue, the Emails card's purple, the two Bugify cards' green
// (menuBubble gives the card's line, and the seam takes that same value, CSS variable or hex). The dashes are the open Drop Folder's and the receipts' bubble's
// — 8px with 5px between, 1px — from the left edge on, as many as the card's width takes; those two measure their width
// so a whole number fits, which a card as wide as the window makes it cannot. It was a plain 1px --c-gray-200 line
// inside the padding (report 7f6e809c, 3rd opening).
const SEAM_DASH = 8, SEAM_GAP = 5;   // stitchPath in FileLibrary, RC_DASH/RC_GAP in Peak — a change there is a change here
const seamLine = (color: React.CSSProperties['borderColor']): React.CSSProperties => ({
  height: 1, flexShrink: 0, margin: '4px -16px',
  background: `repeating-linear-gradient(to right, ${color} 0, ${color} ${SEAM_DASH}px, transparent ${SEAM_DASH}px, transparent ${SEAM_DASH + SEAM_GAP}px)`,
});

export function UserPageView() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const admin = me?.role === 'admin';
  const [who, setWho] = useState('');                                 // the person shown; '' = the signed-in user
  const [page, setPage] = useState<UserPage | null>(null);
  const [people, setPeople] = useState<UserPage['people']>(undefined);   // an admin's picker, kept while the next person loads
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);       // the scores' trial switch, or the team, on its way
  const [trialErr, setTrialErr] = useState(false);
  const [teamErr, setTeamErr] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);   // the Team picker's list of names
  const teamRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    setPage(null);
    setFailed(false);
    setTrialErr(false);
    setTeamErr(false);
    setTeamOpen(false);
    api.getUserPage(who || undefined).then(
      d => { if (!alive) return; setPage(d); if (d.people) setPeople(d.people); },
      () => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [who]);

  // The Team picker's list closes on a click anywhere else.
  useEffect(() => {
    if (!teamOpen) return;
    const onDown = (e: MouseEvent) => { if (!teamRef.current?.contains(e.target as Node)) setTeamOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [teamOpen]);

  // 'Show scores (trial)': an admin switches the scores on or off for the person shown, then their page loads again —
  // the old one stays on screen meanwhile, and the new one is taken only if that person is still the one shown.
  const showScores = (on: boolean) => {
    if (!page || saving) return;
    const id = page.user.id;
    setSaving(true);
    setTrialErr(false);
    api.setUserPageScores(id, on)
      .then(() => api.getUserPage(id))
      .then(d => setPage(cur => (cur?.user.id === d.user.id ? d : cur)), () => setTrialErr(true))
      .finally(() => setSaving(false));
  };

  // 'Team' (report 8832878a): an admin ticks a member of staff onto the team of the person shown, or off it; the whole
  // team is saved and their page loads again, as showScores does.
  const setMember = (memberId: string, on: boolean) => {
    if (!page || saving) return;
    const id = page.user.id;
    const rest = (page.team?.members || []).map(m => m.id).filter(m => m !== memberId);
    setSaving(true);
    setTeamErr(false);
    api.setUserPageTeam(id, on ? [...rest, memberId] : rest)
      .then(() => api.getUserPage(id))
      .then(d => setPage(cur => (cur?.user.id === d.user.id ? d : cur)), () => setTeamErr(true))
      .finally(() => setSaving(false));
  };

  if (!me) return null;
  const n = (v: number) => fmtNumber(v);
  const teamIds = (page?.team?.members || []).map(m => m.id);
  const teamNames = (page?.team?.members || []).map(m => m.name).join(', ');
  const heading: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '22px 0 10px', textAlign: 'center' };
  // A stat block is a sixth of the row (less its five 12px gaps), so the six share one line. It was a quarter, less
  // three gaps, while there were four of them (report 6e2aad53, 2nd opening: "bring the bug bubble up on the same line
  // as the other three" — a card was 260px up to a third, and the Bugs card wrapped under the other three); the
  // Invoices block (report d14ad11b) made it a fifth and the Emails block (that report's 10th opening) a sixth, or
  // either would have wrapped under the row the same way.
  // In the 1694px column a sixth comes out at 272px, the 10px off a block Calem asked for in that opening ("Reduce the
  // width by 10 pixels for each block"): a fifth of the 1460px column was 282px, and 210px while the column was
  // 1100px, which wrapped the longest labels (report d14ad11b's 2nd opening widened the column, its 10th widened it
  // again; this row's own layout is untouched either time). So the longest row labels and headings have 10px less room
  // than they had. It never narrows below 200px: on a window too narrow for six of those the blocks wrap from the
  // middle, so a short last row sits centred rather than at the left. Its heading is a bubble of its own, with the card
  // of its figures 6px under it (report 7f6e809c: "Make the headings of each block a separate bubble and increase the
  // size of the text in the header bubble" — the title was a 13px line at the top of the card, its icon 17px). At 272px
  // the longest of them all, 'System suggestions', still sits on one line, so every heading bubble is the same height
  // again. The card reaches down to the row's tallest, so the cards' bottoms still line up. The heading bubble and the
  // card take their page's menu bubble's line (menuBubble), and the card its fill; the heading bubble takes that fill
  // five shades darker, so the title bar reads as a bar of its own over the figures (report d14ad11b, 3rd opening —
  // Calem: "Make the title bars one shade darker"; both wore the one wash until then — its 4th, one shade having still
  // read too close to the figures box: "Make the title bars another shade darker" — its 5th, two having read too close
  // as well: "Still not dark enough. Please make another shade darker" — and its 6th, three reading that way still:
  // "Another two shades darker - not dark enough"). A shade darker is down lighten's own scale, away from white, the
  // step a hovered row's fill takes (report 28e90b5f) — not darken, whose shades are for a line. "Another shade" is a
  // quarter of the way on from where the bar already stands, so the five shades nest, one lighten(fill, -1) laid on
  // the last ([1,2,3,4,5].reduce), the step darken's own comment prescribes — not a flat lighten(fill, -5), which counts all
  // five quarters from the card's fill and so stops short of it: the Quotations' and Invoices' blue #E6F1FB goes
  // #E0EEFA → #D8EAF9 → #CEE5F8 → #C2DFF6 → #B3D7F4 nested, where a flat -5 lands on #C7E0F6, short of even the fourth
  // nested shade. lighten reads a hex and a see-through 'rgba(…)' alike — and lays an 'rgba(…)' on white, so every call
  // past the first reads a hex — hence the Jobs block's pink, the Emails block's purple and that blue need nothing of
  // their own; the two Bugify blocks' fill is the CSS variable --accent-bg, which it cannot read, so its value goes in
  // its place (ACCENT_WASH), and takes #DAF3EA → #D1F0E5 → #C6ECDF → #B8E7D7 → #A6E1CD. The Jobs pink is so pale a
  // wash — rgba(236,72,153,0.07), all but white on white — that its red stands at the top of the scale from the first
  // shade on and every step deepens its green and blue alone, #FDEFF6 → #FDEBF4 → #FDE6F1 → #FDE0EE → #FDD8EA: it
  // moves less per shade than the others, so its bar is the palest of the six, which is the wash's doing and not this
  // step's. The Emails block's purple is a see-through wash too, rgba(127,119,221,0.094), and its bar lands on #DAD8F6
  // by way of #F0EFFB → #ECEBFA → #E7E6F9 → #E1E0F8.
  // The line, the icon's colour and the words are untouched, as is the card's own fill: the card still hands
  // that line's colour to its body, which draws the seam between its two halves in it (seamLine), and the blocks'
  // heights are as they were.
  // A right-click on a block opens the page it stands for in a browser tab of its own (Calem, report ab347131: "also
  // allow this in the user page"): a block's key IS that page's — Jobs the Dashboard's Active Jobs tab, Emails,
  // Quotations and Invoices their own tabs, the two Bugify blocks Bugify — so its address is the one every menu opens
  // (services/newTab: pageHref), the same key that already gives the block its colours (menuBubble). The blocks stay
  // as they are to a left click: none of them becomes a button.
  // A block counts the person shown, not the person reading, so a login that may not open the page — an employee
  // reading their Quotations block with no Peak permission — keeps the browser's own menu there, rather than a new tab
  // that falls back to some other page; which figures anyone may see at all is the server's to decide
  // (routes/userPage.ts). Read as the Portal's own tabs are (canSeeTab): Quotation and Invoice are the Peak module's
  // pages, and Bugify is every staff login's, behind no permission at all (App.tsx), so it needs no entry.
  const PAGE_PERM: Record<string, string> = { active_jobs: 'active_jobs', quotation: 'peak', invoice: 'peak', email: 'email' };
  const blockUrl = (k: string) => (!PAGE_PERM[k] || admin || me.modules.includes(PAGE_PERM[k]) ? pageHref(k) : '');
  // Named as the page's own top-menu button names it: the Email page is "Email" there, not the "PandaAI™ Email"
  // module name (the browser-tab titles read it the same way, App.tsx DocumentTitle).
  const pageName = (k: string) => t(k === 'email' ? 'tab_email' : `mod_${k}`);
  const statBlock = (k: string, icon: string, label: string, body: (seam: React.CSSProperties['borderColor']) => React.ReactNode) => {
    const { borderColor, background, color } = menuBubble(k);
    const url = blockUrl(k);
    const bubble: React.CSSProperties = { border: '1px solid', borderColor, background, borderRadius: 12 };
    const fill = typeof background === 'string' && !background.startsWith('var(') ? background : ACCENT_WASH;
    const titleFill = [1, 2, 3, 4, 5].reduce(c => lighten(c, -1), fill);   // five nested shades, one laid on the last
    return (
      <div title={url ? t('nt_open', { page: pageName(k) }) : undefined} onContextMenu={url ? openInNewTab(url) : undefined}
        style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200, flex: '1 1 0', maxWidth: 'calc((100% - 60px) / 6)' }}>
        <div style={{ ...bubble, background: titleFill, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
          <i className={`ti ti-${icon}`} style={{ fontSize: 20, color }} aria-hidden />{label}
        </div>
        <div style={{ ...bubble, flexGrow: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>{body(borderColor)}</div>
      </div>
    );
  };
  const row = (label: string, value: React.ReactNode, tip?: string) => (
    <div title={tip} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
      <span style={{ minWidth: 0 }}>{label}</span>
      <strong style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{value}</strong>
    </div>
  );
  // A block's body (report 7f6e809c, 3rd opening: "Merge the user criteria (jobs etc) and the team's criteria (job's etc)
  // into the same block/bubble, separated by a gray line"): the person's rows under a small bold sub-heading — My Jobs, My
  // Quotations… — and, on a page with a team, the card's own stitched seam (seamLine, report 81b2c692), then the team's
  // rows, with the same labels, under Team Jobs, Team Quotations…, whose tooltip names the members. With no team the
  // block shows the 'My' part alone, with no seam.
  const subHeading = (label: string, tip?: string) => (
    <div title={tip} style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>{label}</div>
  );
  // The two halves take the same share of the card's height, however few rows each holds, so the seam runs through the
  // card's middle and every block's seam sits at one height across the row (report 81b2c692, 2nd opening: "Stitched
  // lines through the middle of each block. My KPIs above the stitch, team KPIs below the stitch"). Before that each half
  // was only as tall as its own rows and both stacked at the top of the card, so a block with few 'My' rows — Emails,
  // Suggestions, Bug Fixes — had its seam near the card's top while Jobs' and Quotations' sat halfway down, the card being as tall as
  // the row's tallest either way. The halves and the seam between them are the three rows of a grid, '1fr auto 1fr': a 1fr
  // row shares out the spare height evenly but never falls below its own content, so the halves come out equal AND the
  // card keeps its true height. They were flex items of a zero basis (`flex: '1 1 0'`) for one opening, which left the
  // rows out of the card's height altogether — every card shrank to its padding and seam and its rows spilled out over
  // what came below (report 81b2c692, 3rd opening: "Make all blocks look like the screenshot below").
  // The card's padding is 14px and the grid's gap 8px, so the seam's outer 9px plus the two gaps' 16px leave (H−25)/2 to
  // each half, which puts the seam's line at H/2 — that is why they all line up, and moving either breaks it. The rows
  // themselves still start at the top of their half, the spare height falling below them; whatever a block adds after the
  // halves — the quotations' note — stays outside the grid, at the card's foot. With no team there is no grid and no
  // seam: the 'My' sub-heading and rows sit straight in the card, as they did before it was ever cut in half.
  const half: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };
  const halves = (seam: React.CSSProperties['borderColor'], my: string, myRows: React.ReactNode, ours: string, teamRows?: React.ReactNode) => teamRows == null ? (
    <>
      {subHeading(my)}
      {myRows}
    </>
  ) : (
    <div style={{ display: 'grid', gridTemplateRows: '1fr auto 1fr', gap: 8, flexGrow: 1, minHeight: 0 }}>
      <div style={half}>
        {subHeading(my)}
        {myRows}
      </div>
      <div style={seamLine(seam)} aria-hidden />
      <div style={half}>
        {subHeading(ours, teamNames)}
        {teamRows}
      </div>
    </div>
  );
  // Each block's rows, for the person or the team. 'Jobs as a resource' has its tip in the person's part only: it says
  // "this person".
  const jobRows = (j: UserPage['jobs'], own: boolean) => (
    <>
      {row(t('up_jobs_total'), n(j.total), own ? t('up_jobs_total_tip') : undefined)}
      {row(t('up_jobs_done'), n(j.done), t('up_jobs_done_tip'))}
      {row(t('up_jobs_open'), n(j.total - j.done))}
      {row(t('up_jobs_on_time'), t('up_x_of_y', { x: n(j.onTime), y: n(j.measured) }))}
    </>
  );
  const quoteRows = (q: UserPage['quotations']) => (
    <>
      {row(t('up_quotes_created'), n(q.created))}
      {row(t('up_quotes_pending'), n(q.pending), t('up_quotes_pending_tip'))}
      {row(t('up_quotes_expired'), n(q.expired), t('up_quotes_expired_tip'))}
      {row(t('up_quotes_receipted'), n(q.receipted), t('up_quotes_receipted_tip'))}
    </>
  );
  // The Emails block's one row (report d14ad11b, 9th opening: "Add an email to Quotations block - conversion of emails
  // to quotation"; its 10th: "make a new block for the email function rather than including it in the quotations
  // block") counts leads rather than quotations: the first inquiries this person — or the team — answered themselves,
  // and how many of those a quotation was later written for, as 'X of Y'. The server still serves the pair inside
  // `quotations`, where the 9th opening put it, and it is counted exactly as it was; only its place on screen has moved
  // — out of the Quotations card's fifth row into a card of its own, which the 11th opening set before Quotations. The
  // row names what it counts rather than repeating the block's title, as the Bugs block's one row does, and keeps the
  // tooltip it has always had.
  const emailRows = (q: UserPage['quotations']) =>
    row(t('up_emails_converted'), t('up_x_of_y', { x: n(q.emails.converted), y: n(q.emails.pursued) }), t('up_quotes_emails_tip'));
  // Created · Unpaid · Unpaid over 7 days · Paid (report d14ad11b: "Add invoices to the criteria in between quotations and
  // system suggestions"). Unpaid is what the Invoice list's Unpaid filter gathers, and the third row the pink rows of that
  // list — an unpaid invoice issued 7 or more days ago — so it is counted under Unpaid as well.
  const invoiceRows = (iv: UserPage['invoices']) => (
    <>
      {row(t('up_inv_created'), n(iv.created), t('up_inv_created_tip'))}
      {row(t('up_inv_unpaid'), n(iv.unpaid), t('up_inv_unpaid_tip'))}
      {row(t('up_inv_overdue'), n(iv.overdue), t('up_inv_overdue_tip'))}
      {row(t('up_inv_paid'), n(iv.paid), t('up_inv_paid_tip'))}
    </>
  );
  const suggRows = (s: UserPage['suggestions']) => (
    <>
      {row(t('up_sugg_made'), n(s.made))}
      {row(t('up_sugg_implemented'), n(s.implemented), t('up_sugg_implemented_tip'))}
    </>
  );
  // The quotations' notes the AI has yet to read, the person's and the team's together (never the same quotations: no
  // one is on their own team); while there are any, either part's Pending may still go up.
  const unchecked = (page?.quotations.unchecked || 0) + (page?.team?.quotations.unchecked || 0);

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 28px 28px', background: 'var(--bg-surface)' }}>
      <div style={{ maxWidth: 1694, margin: '0 auto' }}>
        {/* Whose page it is; an admin picks anyone on the staff here, themselves first. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-user" style={{ fontSize: 22 }} aria-hidden />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{t('up_title')}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', minHeight: 22 }}>{page?.user.name || ''}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', minHeight: 16 }}>{page?.user.email || ''}</div>
          </div>
          {admin && people && people.length > 0 && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('up_person')}</span>
                <select value={who || me.userId} onChange={e => setWho(e.target.value === me.userId ? '' : e.target.value)}
                  style={{ padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', maxWidth: 260, cursor: 'pointer' }}>
                  {[...people.filter(p => p.id === me.userId), ...people.filter(p => p.id !== me.userId)].map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
              {/* The scores' trial for the person shown: ticked = their dials show their scores. */}
              <label title={t('up_scores_trial_tip')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', cursor: page && !saving ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={!!page?.scores} disabled={!page || saving} onChange={e => showScores(e.target.checked)}
                  style={{ margin: 0, cursor: 'inherit' }} />
                {t('up_scores_trial')}
              </label>
              {/* The team of the person shown (report 8832878a): whose figures the 'Team' part of each block counts. The
                  button names the members; its list ticks anyone else on the staff onto the team or off it, each tick
                  saved at once. */}
              <div ref={teamRef} style={{ position: 'relative' }}>
                <label title={t('up_team_tip')} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('up_team')}</span>
                  <button type="button" disabled={!page} aria-expanded={teamOpen} onClick={() => setTeamOpen(o => !o)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', maxWidth: 260, cursor: page ? 'pointer' : 'default' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{teamNames || t('up_team_none')}</span>
                    <i className="ti ti-chevron-down" style={{ fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />
                  </button>
                </label>
                {teamOpen && page && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20, minWidth: '100%', width: 'max-content', maxWidth: 300, maxHeight: 300, overflowY: 'auto', padding: '4px 0', background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)' }}>
                    {people.filter(p => p.id !== page.user.id).map(p => (
                      <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontSize: 13, color: 'var(--text-primary)', cursor: saving ? 'default' : 'pointer' }}>
                        <input type="checkbox" checked={teamIds.includes(p.id)} disabled={saving} onChange={e => setMember(p.id, e.target.checked)}
                          style={{ margin: 0, cursor: 'inherit' }} />
                        {p.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {trialErr && (
            <div style={{ width: '100%', textAlign: 'right', fontSize: 12, color: '#B42318' }}>{t('up_scores_trial_failed')}</div>
          )}
          {teamErr && (
            <div style={{ width: '100%', textAlign: 'right', fontSize: 12, color: '#B42318' }}>{t('up_team_failed')}</div>
          )}
        </div>

        {failed ? (
          <div style={{ marginTop: 18, fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '8px 12px' }}>
            <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} aria-hidden />{t('up_load_failed')}
          </div>
        ) : !page ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <i className="ti ti-loader" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
          </div>
        ) : (
          <>
            <div style={heading}>{t('up_scores')}</div>
            {/* On a page with a team, its finishing score has a dial of its own between the two, Team productivity — it
                measures how the team finishes its jobs; there is no team Creativity score, so no team dial for that
                (report 52cf6f71, 2nd opening). Three dials and their two gaps take 588px of the 1694px column. */}
            <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Dial label={t('up_productivity')} value={page.scores?.productivity} tip={page.scores ? t('up_productivity_points_tip') : undefined} />
              {page.team && (
                <Dial label={t('up_team_productivity')} value={page.team.score} tip={page.scores ? t('up_team_score_tip') : undefined} />
              )}
              <Dial label={t('up_creativity')} value={page.scores?.creativity} tip={page.scores ? t('up_creativity_tip') : undefined} />
            </div>

            <div style={heading}>{t('up_stats')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
              {statBlock('active_jobs', 'briefcase', t('up_jobs'), seam => halves(seam,
                t('up_my_jobs'), jobRows(page.jobs, true),
                t('up_team_jobs_sub'), page.team && jobRows(page.team, false)))}
              {/* The leads they answered and how many turned into a quotation, between Jobs and Quotations (report
                  d14ad11b, 10th opening: "make a new block for the email function rather than including it in the
                  quotations block"; its 11th: "Change the order so that the email block is before the quotations
                  block" — it stood straight after Quotations until then). Email shares a top-menu box with Contacts and
                  Calendar, so this block wears that box's purple and the mail icon the Portal gives the page
                  (menuBubble). */}
              {statBlock('email', 'mail', t('up_emails'), seam => halves(seam,
                t('up_my_emails'), emailRows(page.quotations),
                t('up_team_emails'), page.team && emailRows(page.team.quotations)))}
              {/* Created · Pending · Expired · Receipt Sent (report ec7aaa65: Pending and Expired go between Created and the
                  row that read "Turned into a receipt", now "Receipt Sent"). The leads answered that turned into a
                  quotation sat under them for one opening and have the Emails block to themselves from the 10th. Pending
                  is the AI's reading of each open quotation's internal note, so a quotation may be Pending and Expired
                  at once; the AI reads the notes in the background, and while some are still unread the note at the foot
                  of the block says so. */}
              {statBlock('quotation', 'file-dollar', t('up_quotations'), seam => <>
                {halves(seam, t('up_my_quotations'), quoteRows(page.quotations), t('up_team_quotations'), page.team && quoteRows(page.team.quotations))}
                {unchecked > 0 && (
                  <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('up_quotes_unchecked_note', { n: n(unchecked) })}</div>
                )}
              </>)}
              {/* The invoices they issued (report d14ad11b), between Quotations and System suggestions — where that
                  report first asked for it, the Emails block having moved ahead of Quotations in its 11th opening.
                  Invoice sits in the same top-menu box as Quotation, so this block wears that box's blue too
                  (menuBubble). */}
              {statBlock('invoice', 'file-invoice', t('up_invoices'), seam => halves(seam,
                t('up_my_invoices'), invoiceRows(page.invoices),
                t('up_team_invoices'), page.team && invoiceRows(page.team.invoices)))}
              {statBlock('bugify', 'bulb', t('up_suggestions'), seam => halves(seam,
                t('up_my_suggestions'), suggRows(page.suggestions),
                t('up_team_suggestions'), page.team && suggRows(page.team.suggestions)))}
              {/* Still the bugs found and reported in Bugify; only the sub-headings say Bug Fixes, as the reporter worded them. */}
              {statBlock('bugify', 'bug', t('up_bugs'), seam => halves(seam,
                t('up_my_bug_fixes'), row(t('up_bugs_found'), n(page.bugs.found)),
                t('up_team_bug_fixes'), page.team && row(t('up_bugs_found'), n(page.team.bugs.found))))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// A score's circular dial, out of 100: the ring fills value/100 of the way round, clockwise from the top, around the
// figure alone — its "/100" went in report 52cf6f71 ("as it's already obvious"). The arc is drawn a point at a time, each
// piece in the colour of its place on the way round (dialColor), so it starts red at the top and blends on into the
// colour its score has reached. Each piece reaches half a point into the next, which is drawn over it, so no hairline of
// the grey track shows between them. The arc is wider than the track, standing out past it on both sides, and ends in a
// small arrowhead in its end's colour that points on clockwise round the ring (report 52cf6f71, 3rd opening: "Make the
// colored band going around the circle wider than the circle track. And put a small arrowhead on the end of it"). The
// arrowhead starts a pixel back, over the arc's square end, so no hairline shows between them either; a dot of the arc's
// width under its start still rounds it off at the top. Up to that opening the arc was the track's width with a round dot
// at each end. The arc and its arrowhead stand a few px out past the dial's square, so the drawing may spill over its
// edge. No value — the scores' trial is off for this person, or they have fewer than 5 jobs (Productivity), measured team
// jobs (Team productivity) or reports (Creativity) — leaves an empty grey track, with no arc and no arrowhead, whose
// middle says "Not enough data yet".
function Dial({ label, value, tip }: { label: string; value?: number | null; tip?: string }) {
  const { t } = useTranslation();
  const r = (DIAL - RING) / 2;
  const mid = DIAL / 2;
  const v = value != null ? Math.min(value, 100) : 0;
  const pieces = Math.ceil(v);
  const px = 50 / (Math.PI * r);   // the points of the way round that one px along the ring's centre line takes
  // [x, y] `p` points of the way round clockwise from the top, on the ring's centre line or `d` px outside it (inside,
  // if `d` is negative).
  const at = (p: number, d = 0) => [mid + (r + d) * Math.sin(Math.PI * p / 50), mid - (r + d) * Math.cos(Math.PI * p / 50)];
  return (
    <div title={tip} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <div style={{ position: 'relative', width: DIAL, height: DIAL }}>
        <svg width={DIAL} height={DIAL} viewBox={`0 0 ${DIAL} ${DIAL}`} style={{ overflow: 'visible' }} aria-hidden>
          <circle cx={mid} cy={mid} r={r} fill="none" stroke="var(--c-gray-200)" strokeWidth={RING} />
          {pieces > 0 && <circle cx={mid} cy={mid - r} r={ARC / 2} fill={dialColor(0)} />}
          {Array.from({ length: pieces }, (_, i) => (
            <path key={i} d={`M ${at(i).join(' ')} A ${r} ${r} 0 0 1 ${at(Math.min(i + 1.5, v)).join(' ')}`} fill="none"
              stroke={dialColor((i + Math.min(i + 1, v)) / 2)} strokeWidth={ARC} />
          ))}
          {pieces > 0 && (
            <polygon points={[at(v - px, -HEAD_W / 2), at(v + HEAD_L * px), at(v - px, HEAD_W / 2)].map(q => q.join(',')).join(' ')}
              fill={dialColor(v)} />
          )}
        </svg>
        <div style={{ position: 'absolute', inset: RING + 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, color: 'var(--text-tertiary)' }}>
          {value == null ? t('up_no_score') : (
            <span style={{ fontSize: 38, fontWeight: 800, lineHeight: 1, color: 'var(--text-primary)' }}>{fmtNumber(value)}</span>
          )}
        </div>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>{label}</div>
    </div>
  );
}
